import * as z from "zod";
import { creditNoteDocumentDto } from "../documents/credit-note-document";
import { anyOfFilters, countByFiltersInput, decimal, flag, pagination, version } from "../index";
import { creditNoteStatuses } from "./enums";
import { poReason } from "./purchase-order";

// Credit notes of the missing units of an inbound (50-api/procurement.md §2.6; FR-M11-11, ADR-140): the supplier owes
// one when it chose a credit note, sends its file, and Finance receives it or rejects it. From F2 (B-54 point 3,
// ADR-230) receiving it is Finance's approval: documents asks Business Central for the credit note (I-04
// `credit_note`), which comes back numbered.

const creditNoteSorts = ["-updatedAt", "requestedAt"] as const;

const creditNoteFilters = {
  status: z.enum(creditNoteStatuses).optional(),
  brand: z.uuid().optional(),
  supplier: z.uuid().optional(),
  /** Its file is there and Finance has not decided yet (`canDecideCreditNote`). */
  toReview: flag.optional(),
  /** Waits for the supplier's file: never sent, or rejected by Finance. */
  toSend: flag.optional(),
  /**
   * The file still owed a cadence after the last reminder of its series (B-41, ADR-245): Finance's queue, which chases
   * the supplier.
   */
  remindersOver: flag.optional(),
  /**
   * The supplier's side (ADR-170): the credit notes of the suppliers the Actor works for, those it sends. For Tomorrow
   * it takes nothing out.
   */
  asSupplier: flag.optional(),
  /** Part of the number of the inbound, by its letters and digits (ADR-117). */
  q: z.string().trim().min(1).max(30).optional(),
};

/** The credit notes in at least one of these sets of the filters (ADR-302): the queues of the credit notes, to do. */
const anyOfCreditNotes = { anyOf: anyOfFilters(creditNoteFilters).optional() };

/** The credit notes the Actor sees: those of the inbounds it sees. */
export const listCreditNotesInput = z.object({
  ...creditNoteFilters,
  ...anyOfCreditNotes,
  sort: z.enum(creditNoteSorts).default("-updatedAt"),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListCreditNotesInput = z.input<typeof listCreditNotesInput>;

/** How many credit notes the list has with the same filters (ADR-086). */
export const countCreditNotesInput = z.object({ ...creditNoteFilters, ...anyOfCreditNotes });
export type CountCreditNotesInput = z.input<typeof countCreditNotesInput>;
/** The same count under several filters, in one call and one transaction (ADR-269): the queues of the home. */
export const countCreditNotesByFiltersInput = countByFiltersInput(countCreditNotesInput);
export type CountCreditNotesByFiltersInput = z.input<typeof countCreditNotesByFiltersInput>;

/** An action on a credit note. `version` is the one the client read: one changed in the meantime answers 409. */
export const creditNoteActionInput = z.object({ creditNoteId: z.uuid(), version });
export type CreditNoteActionInput = z.input<typeof creditNoteActionInput>;
export const creditNoteParams = creditNoteActionInput.pick({ creditNoteId: true });
export const creditNoteVersionBody = creditNoteActionInput.pick({ version: true });

/** The file of the credit note, already uploaded in the `inbound` folder (ADR-069): it replaces the one before. */
export const sendCreditNoteInput = creditNoteActionInput.extend({ fileId: z.uuid() });
export type SendCreditNoteInput = z.input<typeof sendCreditNoteInput>;
export const sendCreditNoteBody = sendCreditNoteInput.omit({ creditNoteId: true });

/** Why Finance rejects a credit note: the supplier reads it, and sends a new file. */
export const rejectCreditNoteInput = creditNoteActionInput.extend({ reason: poReason });
export type RejectCreditNoteInput = z.input<typeof rejectCreditNoteInput>;
export const rejectCreditNoteBody = rejectCreditNoteInput.omit({ creditNoteId: true });

/** The document of the inbound that holds the file of a credit note, which its inbound serves. */
const creditNoteFileDto = z.object({ documentId: z.uuid(), filename: z.string() });

/** A credit note of the missing units of an inbound: in the list, in its inbound and after an action. */
export const creditNoteDto = z
  .object({
    id: z.uuid(),
    inboundId: z.uuid(),
    inboundNumber: z.string(),
    poId: z.uuid(),
    poNumber: z.string(),
    brandOrgId: z.uuid(),
    brand: z.string(),
    supplierOrgId: z.uuid(),
    supplier: z.string(),
    /** The Actor answers for it as its supplier (`actsFor`, ADR-170): only then it sends its file. */
    asSupplier: z.boolean(),
    status: z.enum(creditNoteStatuses),
    /** The missing units at the cost of their PO lines, in the currency of the PO, computed at the request. */
    amount: decimal.nullable(),
    currency: z.string().nullable(),
    /** Null until the supplier sends it. */
    file: creditNoteFileDto.nullable(),
    requestedAt: z.iso.datetime(),
    receivedAt: z.iso.datetime().nullable(),
    /** Who received or rejected it last, and when. */
    decidedBy: z.string().nullable(),
    decidedAt: z.iso.datetime().nullable(),
    /** Why Finance rejected it, while it waits for a new file. */
    rejectionReason: z.string().nullable(),
    /**
     * F2 (ADR-230), Tomorrow's: the credit note documents asked Business Central for once Finance received the
     * supplier's one, with its state and the number BC gave it.
     */
    bcCreditNote: creditNoteDocumentDto.nullable().optional(),
    version: z.number().int(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "CreditNote" });
export type CreditNoteDto = z.output<typeof creditNoteDto>;
